// Searching INSIDE the files attached to records.
//
// WHAT MAKES IT DIFFERENT FROM THE FILTERS BESIDE IT. The sidebar and the grid's own search read
// SEARCH_CONTENT, which indexes a record's METADATA -- its number, name, description and form
// answers. Nothing looks inside an attached file. This does, and two ways at once: by MEANING,
// where "how long are records kept" finds a procedure that says "retained for seven years" though
// neither phrase contains the other; and by WORDING, where "SOP-00009" finds that exact token,
// which is the half an embedding is worst at. The server fuses the two rankings and says which one
// found each hit.
//
// SCOPED TO ONE CLASSIFICATION, and that is deliberate rather than a limitation. Attachments hang
// off ten record types in this estate -- Document 140, Item 118, Inventory 74, Compliance 41,
// PurchaseOrder 33 and on down -- so a box on the Documents page that searched all of them would
// answer a question about Documents with a Purchase Order. Ed, 2026-10-21: "Am I searching
// Documents or Items?" The answer has to be visible from where you are standing.
//
// The FoundationAPI client, which attaches the active persona: the corpus is scoped by the asker's
// own view, and t1's door is the only one that verifies the persona a request claims.
import {makeCresAxiosFoundationAPI} from "../util/AxiosFoundationAPI";

/** One matching passage, and the record whose file it came from. */
export type AttachmentHit = {
    chunkUSID: string;
    attachmentUSID: string;
    objectRevisionUSID: string;
    objectID: string | null;
    recordName: string | null;
    displayRevision: string | null;
    fileName: string;
    /** 1-indexed. Null where the file had no usable page structure — see the salvage extractor. */
    page: number | null;
    text: string;
    /**
     * 0..1, bigger is closer -- or NULL when the vector arm did not find this hit.
     *
     * NULL IS NOT ZERO, and the server is careful about the difference: nothing measured this
     * row's similarity, which is not the same claim as "measured, and completely dissimilar". A
     * keyword-only hit has no similarity and must not be drawn as a weak one.
     */
    similarity: number | null;
    /** The fused rank score. For ORDERING only -- it has no units and is not a percentage. */
    score: number;
    /** Which arms found it: "meaning", "wording", or both. Both is the strongest result. */
    foundBy: ("meaning" | "wording")[];
};

/** How the ranking was produced. Never inferred on this side -- the server says. */
export type SearchMode = "hybrid" | "semantic" | "keyword";

export type AttachmentSearchResult = {
    query: string;
    /** What was actually searched. Null means every record type. */
    objectClassification: string | null;
    model: string | null;
    /**
     * HOW it was ranked, always -- not only when something went wrong.
     *
     * A keyword-only answer and a hybrid one look identical on screen, and the difference decides
     * whether a paraphrase of the question could have been found at all. On this account the
     * embedding model's quota is zero, so "keyword" is the normal case rather than the exception.
     */
    mode: SearchMode;
    /** Why the mode is not hybrid, in the server's own words. Null when nothing is wrong. */
    note: string | null;
    hits: AttachmentHit[];
};

export async function searchAttachments(
    sessionContext: any, query: string, objectClassification: string | null, limit = 10,
): Promise<AttachmentSearchResult> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    const res = await api({
        method: "POST",
        url: "t1/v1/attachment_search",
        data: {Query: query, ObjectClassification: objectClassification, Limit: limit},
    });
    const body = res?.data ?? {};
    return {
        query: String(body.Query ?? query),
        objectClassification: body.ObjectClassification ? String(body.ObjectClassification) : null,
        model: body.Model ? String(body.Model) : null,
        // DEFAULTED TO THE DEGRADED READING, not the healthy one. An older backend that does not
        // send Mode is one that cannot have fused anything, so "hybrid" would be a claim this
        // response does not support.
        mode: (body.Mode === "hybrid" || body.Mode === "semantic") ? body.Mode : "keyword",
        note: body.Note ? String(body.Note) : null,
        hits: (Array.isArray(body.Results) ? body.Results : []).map((one: any) => ({
            chunkUSID: String(one?.ChunkUSID ?? ""),
            attachmentUSID: String(one?.AttachmentUSID ?? ""),
            objectRevisionUSID: String(one?.ObjectRevisionUSID ?? ""),
            objectID: one?.ObjectID ? String(one.ObjectID) : null,
            recordName: one?.RecordName ? String(one.RecordName) : null,
            displayRevision: one?.DisplayRevision ? String(one.DisplayRevision) : null,
            fileName: String(one?.FileName ?? ""),
            page: typeof one?.Page === "number" ? one.Page : null,
            text: String(one?.Text ?? ""),
            similarity: typeof one?.Similarity === "number" ? one.Similarity : null,
            score: typeof one?.Score === "number" ? one.Score : 0,
            foundBy: (Array.isArray(one?.FoundBy) ? one.FoundBy : [])
                .filter((name: any) => name === "meaning" || name === "wording"),
        })),
    };
}
